import { ActionIcon, Indicator, Popover, Stack, Text, Group, ScrollArea } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconBell } from '@tabler/icons-react';
import { useNotifications, useMarkNotificationRead } from '@/hooks/usePortalApi';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

export default function NotificationBell() {
  const [opened, { toggle, close }] = useDisclosure(false);
  const { data: notifData } = useNotifications();
  const markRead = useMarkNotificationRead();

  const notifications = Array.isArray(notifData) ? notifData : (notifData as any)?.items ?? [];
  const unreadCount = notifications.filter((n: any) => !n.read).length;

  return (
    <Popover
      opened={opened}
      onChange={(o) => { if (!o) close(); }}
      width={320}
      position="bottom-end"
      shadow="md"
    >
      <Popover.Target>
        <Indicator label={unreadCount} size={18} disabled={unreadCount === 0} color="red">
          <ActionIcon
            variant="subtle"
            color="gray"
            size="lg"
            onClick={toggle}
            aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
          >
            <IconBell size={22} />
          </ActionIcon>
        </Indicator>
      </Popover.Target>
      <Popover.Dropdown p={0}>
        <Text fw={600} p="sm" pb={0}>
          Notifications
        </Text>
        <ScrollArea.Autosize mah={300} aria-live="polite">
          {!notifications?.length ? (
            <Text c="dimmed" size="sm" p="sm">
              No notifications
            </Text>
          ) : (
            <Stack gap={0} role="list" aria-label="Notification list">
              {notifications.map((n: { id: number; title?: string; message: string; read: boolean; created_at: string }) => (
                <Group
                  key={n.id}
                  p="sm"
                  wrap="nowrap"
                  role="listitem"
                  style={{
                    cursor: 'pointer',
                    backgroundColor: n.read ? undefined : 'var(--mantine-color-brand-0)',
                  }}
                  onClick={() => {
                    if (!n.read) markRead.mutate(n.id);
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" fw={n.read ? 400 : 600} lineClamp={1}>
                      {n.title}
                    </Text>
                    <Text size="xs" c="dimmed" lineClamp={2}>
                      {n.message}
                    </Text>
                    <Text size="xs" c="dimmed" mt={2}>
                      {dayjs(n.created_at).fromNow()}
                    </Text>
                  </div>
                </Group>
              ))}
            </Stack>
          )}
        </ScrollArea.Autosize>
      </Popover.Dropdown>
    </Popover>
  );
}
